import { env } from "../env/client";

/**
 * Single place for the numbers and links the marketing site prints.
 *
 * Marketing claims live here so finance/product can change them without
 * touching layout. Everything the copy interpolates (`{{maxAmount}}`,
 * `{{maxYears}}`, …) is read from this file.
 */

/** Facts that appear in the Unlock copy. */
export const UNLOCK = {
  /** Maximum financing, in EGP. */
  maxAmount: 60_000_000,
  /** Minimum financing, in EGP (mirrors `MINIMUM_LOAN_AMOUNT` in the data model). */
  minAmount: 500_000,
  /** Longest tenor, in years. */
  maxYears: 10,
  /** Shortest tenor, in years, offered in the estimator. */
  minYears: 1,
  /** Share of the property value that can be financed. */
  maxLoanToValue: 0.8,
  /** Fastest approval we commit to in copy, in days. */
  approvalDays: 3,
  /**
   * Indicative annual rate used only by the on-site estimator. The real rate
   * is set per application in the LMS from the CBE corridor plus MNZL's
   * spread, and is always shown in the app before anything is accepted.
   */
  indicativeAnnualRate: 0.25,
} as const;

export const MORTGAGE = {
  maxLoanToValue: 0.8,
  maxYears: 10,
  approvalDays: 3,
} as const;

export const AUTO_LOAN = {
  /** Mirrors `MAXIMUM_AUTO_LOAN_AMOUNT`. */
  maxAmount: 7_500_000,
  /** Mirrors `MINIMUM_AUTO_LOAN_AMOUNT`. */
  minAmount: 250_000,
  /** Mirrors `MAX_AUTO_LOAN_TERM_MONTHS` (84 months). */
  maxYears: 7,
} as const;

export const FLEX = {
  /**
   * Whether the developer partnerships are public: the Flex and developer
   * pages, the partner band, and the links and milestone that name them.
   * Off, those pages are 404s and the rest of the site does not mention
   * them. Set per environment in `deploy/.env.*` (on for staging).
   */
  live: env.NEXT_PUBLIC_FLEX_LIVE === "true",
  /** Flex financing lands the day after approval. */
  financingDays: 1,
  /** Pay Less is live for partner homeowners once this flips. */
  payLessLive: false,
} as const;

export const STORE_LINKS = {
  apple: "https://apps.apple.com/eg/app/mnzl-wallet/id6477818458",
  android: "https://play.google.com/store/apps/details?id=co.mnzl.wallet",
} as const;

export const SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/profile.php?id=100094671844602",
  instagram: "https://www.instagram.com/mymnzl/",
  linkedin: "https://www.linkedin.com/company/mnzl",
} as const;

export const CONTACT = {
  email: "hello@mnzl.co",
  phone: "+201020034550",
  phoneDisplay: "+20 10 2003 4550",
  /** The office on Google Maps, by its place id so the pin survives a rename. */
  mapsUrl:
    "https://www.google.com/maps/search/?api=1&query=Canan%20Capital&query_place_id=ChIJzxuZtWA5WBQRHL1Wv0Rx0s0",
  careersUrl:
    "https://mnzl.notion.site/Careers-543d2c1576ce4da2a91a626c98658158",
} as const;

/**
 * Our licences in the FRA register (https://fra.gov.eg/company_records/), one
 * per activity, each with its own record, plus the commercial register and
 * tax numbers printed beside them. The registered names are in the locale
 * files (`site.legal.entity`).
 */
export const FRA = {
  realEstateFinance: {
    licence: "33",
    record: "https://fra.gov.eg/company_records/company-record-33n/",
  },
  consumerFinance: {
    licence: "57",
    record: "https://fra.gov.eg/company_records/company-record-57n-2/",
  },
  commercialRegister: "437701",
  taxId: "516-012-773",
  /**
   * The FRA's client protection guide for financing activities and its
   * summary leaflet, which the FRA requires on the website's home page
   * (Chairman's decree 446 of 2019, article 2). Published by the FRA in
   * Arabic only; served from `public/fra`.
   */
  clientProtection: {
    guide: "/fra/client-protection-guide.pdf",
    leaflet: "/fra/client-protection-leaflet.pdf",
  },
} as const;

/**
 * Slogan. The first entry is live; the rest are the alternatives that were
 * considered so swapping is a one-line change in `locales/*.json`
 * (`site.home.hero.title`).
 *
 * 1. "Your home has grown in value. Use it without selling it."
 * 2. "Your property is worth more than a place to live."
 * 3. "The wealth in your walls, unlocked."
 * 4. "Own more than you think."
 */
export const SLOGAN_KEY = "site.home.hero.title" as const;

export const SITE_URL = "https://mnzl.co";
